import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { Download, Loader2, Users, ArrowLeft, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select'
import { useStages } from '@/hooks/use-picklists'
import { ReportFilters, getDateRange as getReportDateRange } from '@/components/ReportFilters'
import type { ReportFilterValues } from '@/components/ReportFilters'
import { CHART_COLORS } from '@/lib/theme'
import { formatCurrency, formatDate } from '@/lib/utils'
import { exportToCSV } from '@/utils/csvExport'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'
const COLORS = CHART_COLORS

export default function MTDTeams() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<ReportFilterValues>(() => getReportDateRange('this_month'))
  const [stageNames, setStageNames] = useState<string[]>([])
  const [probabilityPreset, setProbabilityPreset] = useState('')
  const { stages } = useStages()
  const [sortField, setSortField] = useState('owner_name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  // Parse probability preset into min/max
  const probFilter = (() => {
    switch (probabilityPreset) {
      case 'lt50':
        return { probability_max: '49' }
      case 'gte50':
        return { probability_min: '50' }
      case 'gte75':
        return { probability_min: '75' }
      case '100':
        return { probability_min: '100', probability_max: '100' }
      default:
        return {}
    }
  })()

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'mtd-teams', filters, stageNames, probabilityPreset],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters.startDate) params.set('startDate', filters.startDate)
      if (filters.endDate) params.set('endDate', filters.endDate)
      if (filters.owner_id) params.set('owner_id', filters.owner_id)
      // Repeated stage_name params → backend reads them as an array (empty = all stages).
      stageNames.forEach((s) => params.append('stage_name', s))
      if (probFilter.probability_min) params.set('probability_min', probFilter.probability_min)
      if (probFilter.probability_max) params.set('probability_max', probFilter.probability_max)
      const response = await (apiClient as any).client.get(
        `/reports/mtd-teams?${params.toString()}`
      )
      return response.data?.data
    },
  })

  const chartData = useMemo(() => {
    if (!data?.summary) return []
    return data.summary.map((item: any) => ({
      name:
        item.owner_name?.length > 18
          ? item.owner_name.substring(0, 18) + '...'
          : item.owner_name || 'Unknown',
      amount: parseFloat(item.total_amount) || 0,
    }))
  }, [data])

  // Group details by owner for subtotals
  const groupedDetails = useMemo(() => {
    if (!data?.details) return []
    const sorted = [...data.details].sort((a: any, b: any) => {
      const aVal = (a[sortField] ?? '').toString().toLowerCase()
      const bVal = (b[sortField] ?? '').toString().toLowerCase()
      // Numeric sort for amount, probability, weighted_amount
      if (['amount', 'probability', 'weighted_amount'].includes(sortField)) {
        const diff = (parseFloat(aVal) || 0) - (parseFloat(bVal) || 0)
        return sortOrder === 'asc' ? diff : -diff
      }
      const cmp = aVal.localeCompare(bVal)
      return sortOrder === 'asc' ? cmp : -cmp
    })

    // Group by owner using a Map to ensure all rows for the same owner stay together,
    // regardless of the sort order (e.g., if sorted by amount, all Christie deals are merged).
    type OwnerGroup = {
      owner_name: string
      owner_id: string
      rows: any[]
      total_amount: number
      weighted_amount: number
    }
    const ownerMap = new Map<string, OwnerGroup>()
    for (const row of sorted) {
      const key = row.owner_id ?? '__unknown__'
      if (!ownerMap.has(key)) {
        ownerMap.set(key, {
          owner_name: row.owner_name || 'Unknown',
          owner_id: row.owner_id,
          rows: [],
          total_amount: 0,
          weighted_amount: 0,
        })
      }
      const group = ownerMap.get(key)!
      group.rows.push(row)
      group.total_amount += parseFloat(row.amount) || 0
      group.weighted_amount += parseFloat(row.weighted_amount) || 0
    }
    return Array.from(ownerMap.values())
  }, [data?.details, sortField, sortOrder])

  const handleExport = () => {
    if (data?.details) {
      exportToCSV({
        filename: 'MTD_Teams_Opportunities',
        data: data.details,
        columns: [
          'owner_name',
          'account_name',
          'name',
          'stage_name',
          'amount',
          'close_date',
          'probability',
          'weighted_amount',
        ],
        headers: [
          'Owner',
          'Account',
          'Opportunity',
          'Stage',
          'Amount',
          'Close Date',
          'Probability %',
          'Weighted Amount',
        ],
      })
    }
  }

  const columns = [
    { field: 'account_name', label: 'Account Name' },
    { field: 'name', label: 'Opportunity Name' },
    { field: 'stage_name', label: 'Stage' },
    { field: 'amount', label: 'Amount' },
    { field: 'close_date', label: 'Close Date' },
    { field: 'probability', label: 'Probability' },
    { field: 'weighted_amount', label: 'Weighted Amount' },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/reports')}
            aria-label="Back to reports"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-[#6D2E6E]" aria-hidden="true" />
              <h1 className="text-2xl font-bold">MTD - Teams</h1>
              <Badge variant="secondary">Pre-built</Badge>
            </div>
            <p className="text-muted-foreground mt-1">
              Month-to-date opportunities by team member
              {data?.totalRecords ? ` — ${data.totalRecords.toLocaleString()} records` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <ReportFilters
            value={filters}
            onChange={setFilters}
            defaultPeriod="this_month"
            datePresets={[
              'today',
              'this_month',
              'last_month',
              'this_quarter',
              'fiscal_year',
              'calendar_year',
              'custom',
            ]}
          />
          <div>
            <Label htmlFor="mtd-stage" className="text-xs text-muted-foreground mb-1 block">
              Stage
            </Label>
            <SearchableMultiSelect
              id="mtd-stage"
              label="Stage"
              options={stages.map((s) => ({ value: s.value, label: s.label }))}
              selected={stageNames}
              onChange={setStageNames}
              triggerClassName="w-[170px] h-9"
            />
          </div>
          <div>
            <Label htmlFor="mtd-probability" className="text-xs text-muted-foreground mb-1 block">
              Probability
            </Label>
            <Select value={probabilityPreset} onValueChange={setProbabilityPreset}>
              <SelectTrigger
                id="mtd-probability"
                className="w-[150px] h-9"
                aria-label="Filter by probability"
              >
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_prob">All</SelectItem>
                <SelectItem value="lt50">&lt; 50%</SelectItem>
                <SelectItem value="gte50">&ge; 50%</SelectItem>
                <SelectItem value="gte75">&ge; 75%</SelectItem>
                <SelectItem value="100">100% (Closed Won)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={!data?.details?.length}
            aria-label="Export to CSV"
            className="self-end"
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Records</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{(data.totalRecords ?? 0).toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Amount</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{formatCurrency(data.grandTotalAmount ?? 0)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Weighted Amount</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{formatCurrency(data.grandWeightedAmount ?? 0)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
          <span className="ml-2">Loading report...</span>
        </div>
      )}

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Error loading report. Please try again.</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && data && (
        <>
          {/* Bar Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Total Amount by Owner</CardTitle>
              <CardDescription>Opportunity value per team member</CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 40)}>
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                    <Tooltip
                      content={<ChartTooltipContent />}
                      formatter={(value: number) => formatCurrency(value)}
                    />
                    <Bar dataKey="amount" fill={COLORS[0]} name="Amount" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center py-8 text-muted-foreground">
                  No data for selected period
                </p>
              )}
            </CardContent>
          </Card>

          {/* Detail Table grouped by owner */}
          <Card>
            <CardHeader>
              <CardTitle>Opportunity Details</CardTitle>
              <CardDescription>
                {data.details?.length || 0} records{' '}
                {data.details?.length >= 500 ? '(showing first 500)' : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {columns.map((col) => (
                        <TableHead key={col.field}>
                          <button
                            className="flex items-center gap-1 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary rounded"
                            onClick={() => handleSort(col.field)}
                            aria-label={`Sort by ${col.label}`}
                          >
                            {col.label}
                            {sortField === col.field ? (
                              sortOrder === 'asc' ? (
                                <ArrowUp className="h-3 w-3" aria-hidden="true" />
                              ) : (
                                <ArrowDown className="h-3 w-3" aria-hidden="true" />
                              )
                            ) : (
                              <ArrowUpDown className="h-3 w-3 opacity-30" aria-hidden="true" />
                            )}
                          </button>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupedDetails.map((group) => (
                      <>
                        {/* Owner group header */}
                        <TableRow key={`header-${group.owner_id}`} className="bg-muted/50">
                          <TableCell colSpan={7} className="font-semibold py-2">
                            {group.owner_name} ({group.rows.length})
                          </TableCell>
                        </TableRow>
                        {/* Opportunity rows */}
                        {group.rows.map((row: any) => (
                          <TableRow key={row.id}>
                            <TableCell>
                              {row.account_id ? (
                                <Link
                                  to={`/accounts/${row.account_id}`}
                                  className="text-primary hover:underline"
                                  aria-label={`View account: ${row.account_name}`}
                                >
                                  {row.account_name || '-'}
                                </Link>
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>
                              <Link
                                to={`/opportunities/${row.id}`}
                                className="text-primary hover:underline"
                                aria-label={`View opportunity: ${row.name}`}
                              >
                                {row.name}
                              </Link>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">{row.stage_name || '-'}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {row.amount != null ? formatCurrency(parseFloat(row.amount)) : '-'}
                            </TableCell>
                            <TableCell>{formatDate(row.close_date)}</TableCell>
                            <TableCell className="text-right">
                              {row.probability != null ? `${parseFloat(row.probability)}%` : '-'}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {row.weighted_amount != null
                                ? formatCurrency(parseFloat(row.weighted_amount))
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Subtotal row */}
                        <TableRow
                          key={`subtotal-${group.owner_id}`}
                          className="border-t-2 font-semibold"
                        >
                          <TableCell colSpan={3} className="text-right">
                            Subtotal
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(group.total_amount)}
                          </TableCell>
                          <TableCell colSpan={2} />
                          <TableCell className="text-right font-mono">
                            {formatCurrency(group.weighted_amount)}
                          </TableCell>
                        </TableRow>
                      </>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
